/**
 * @fileoverview AppShell — Persistent authenticated application wrapper
 *
 * Renders the sidebar (desktop/tablet) and bottom tab bar (mobile),
 * the theme toggle, logout, and a content slot for child pages.
 *
 * Usage in App.jsx:
 *   <AppShell><DashboardPage /></AppShell>
 *
 * The shell reads theme from useTheme() and exposes the toggle via
 * the sidebar bottom controls. Chat drawer is mounted here so it
 * persists across page navigation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate }          from 'react-router-dom';
import { onAuthStateChanged, signOut }             from 'firebase/auth';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db }                                from '../../services/firebase';
import { getProfileImageUrl, DEFAULT_PROFILE_IMAGE } from '../../utils/profileImage';
import { writePresence }                           from '../../utils/presence';
import { useTheme }                                from '../../hooks/useTheme';
import { ChatDrawerProvider, useChatDrawer }        from '../../context/ChatDrawerContext';
import ChatDrawer                                  from './ChatDrawer';
import ContactModal                               from '../ContactModal';
import NotificationToastHost                       from '../notifications/NotificationToastHost';
import NotificationBell                            from '../notifications/NotificationBell';
import './AppShell.css';

// ─── Nav config ───────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { path: '/dashboard',  icon: '🏇', label: 'Dashboard',    exact: true,  built: true  },
  { path: '/showdowns',  icon: '🏆', label: 'Showdowns',    exact: false, built: true  },
  { path: '/my-stable',  icon: '🐎', label: 'My Stable',    exact: false, built: true  },
  { path: '/market',     icon: '📈', label: 'Market',       exact: false, built: true  },
  { path: '/leaderboards',icon: '🥇', label: 'Leaderboards', exact: false, built: true  },
  { path: '/community',  icon: '👥', label: 'Community',    exact: false, built: false },
  { path: '/learn',      icon: '🎓', label: 'Learn',        exact: false, built: false },
  { path: '/account',    icon: '👤', label: 'Account',      exact: false, built: true  },
];

// Bottom tab bar — 5 items only (mobile)
const TAB_ITEMS = [
  { path: '/dashboard',  icon: '🏇', label: 'Dashboard'   },
  { path: '/showdowns',  icon: '🏆', label: 'Showdowns'   },
  { path: '/my-stable',  icon: '🐎', label: 'Stable'      },
  { path: '/leaderboards',icon: '🥇', label: 'Leaderboard' },
  { path: '/account',    icon: '👤', label: 'Account'     },
];

// ─── Coming Soon Toast ────────────────────────────────────────────────────────

const ComingSoonToast = ({ label, onDismiss }) => (
  <div className="ss-shell-toast" onClick={onDismiss}>
    <span className="ss-shell-toast-icon">🚧</span>
    <span><strong>{label}</strong> — coming soon</span>
  </div>
);

// ─── Sidebar Nav Item ─────────────────────────────────────────────────────────

const NavItem = ({ item, isActive, collapsed, onComingSoon }) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (!item.built) {
      e.preventDefault();
      onComingSoon(item.label);
      return;
    }
    navigate(item.path);
  };

  return (
    <button
      className={`ss-nav-item ${isActive ? 'ss-nav-item--active' : ''} ${!item.built ? 'ss-nav-item--soon' : ''}`}
      onClick={handleClick}
      title={collapsed ? item.label : undefined}
      type="button"
    >
      <span className="ss-nav-icon">{item.icon}</span>
      {!collapsed && (
        <span className="ss-nav-label">{item.label}</span>
      )}
      {!collapsed && !item.built && (
        <span className="ss-nav-soon-badge">Soon</span>
      )}
    </button>
  );
};

// ─── FriendBadgeSync ──────────────────────────────────────────────────────────
// Must live inside <ChatDrawerProvider> to call useChatDrawer().
// Drives all three per-tab unread counters (Friends, Teams, Requests).
// Lounge has no badge by design.

function FriendBadgeSync() {
  const { setFriendsUnread, setTeamsUnread, setRequestsUnread } = useChatDrawer();
  const [currentUser, setCurrentUser] = useState(null);
  const [friendUids,  setFriendUids]  = useState([]);

  useEffect(() => onAuthStateChanged(auth, setCurrentUser), []);

  // ── Requests tab — pending incoming friend requests ───────────────
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'friend_requests'),
      where('toUserId', '==', currentUser.uid),
      where('status',   '==', 'pending')
    );
    return onSnapshot(q, snap => setRequestsUnread(snap.size), () => {});
  }, [currentUser, setRequestsUnread]);

  // ── Get friend UID list from subcollection ────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    return onSnapshot(
      collection(db, 'friends', currentUser.uid, 'list'),
      snap => setFriendUids(snap.docs.map(d => d.data().uid)),
      () => {}
    );
  }, [currentUser]);

  // ── Friends badge — one listener per conversation, by known ID ────
  // Uses allow get (single-doc) not allow list — safe with conversations rules.
  // Excludes messages sent by the current user (lastSenderId check).
  useEffect(() => {
    if (!currentUser || friendUids.length === 0) {
      setFriendsUnread(0);
      return;
    }

    const unreadState = {};

    function recompute() {
      setFriendsUnread(Object.values(unreadState).filter(Boolean).length);
    }

    const unsubs = friendUids.map(friendUid => {
      const convoId = [currentUser.uid, friendUid].sort().join('_');
      return onSnapshot(doc(db, 'conversations', convoId), snap => {
        if (!snap.exists()) {
          unreadState[convoId] = false;
          recompute();
          return;
        }
        const data    = snap.data();
        const msgAt   = data.lastMessageAt;
        const readAt  = data.lastReadAt?.[currentUser.uid];
        const isUnread =
          !!msgAt &&
          data.lastSenderId !== currentUser.uid &&
          (!readAt || msgAt.toMillis() > readAt.toMillis());
        unreadState[convoId] = isUnread;
        recompute();
      }, () => { unreadState[convoId] = false; recompute(); });
    });

    return () => unsubs.forEach(u => u());
  }, [currentUser, friendUids, setFriendsUnread]);

  // ── Teams badge — collection query is fine (teams uses array-contains) ──
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'teams'),
      where('members', 'array-contains', currentUser.uid)
    );
    return onSnapshot(q, snap => {
      const unread = snap.docs.filter(d => {
        const data   = d.data();
        const msgAt  = data.lastMessageAt;
        const readAt = data.lastReadAt?.[currentUser.uid];
        if (!msgAt) return false;
        if (data.lastSenderId === currentUser.uid) return false;
        return !readAt || msgAt.toMillis() > readAt.toMillis();
      }).length;
      setTeamsUnread(unread);
    }, () => {});
  }, [currentUser, setTeamsUnread]);

  return null;
}

// ─── AppShell ─────────────────────────────────────────────────────────────────

export const AppShell = ({ children }) => {
  const location                  = useLocation();
  const navigate                  = useNavigate();
  const { theme, toggle, isDark } = useTheme();

  const [currentUser,      setCurrentUser]      = useState(null);
  const [userProfile,      setUserProfile]      = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toast,            setToast]            = useState(null);
  const [pendingAlerts,    setPendingAlerts]    = useState(0);
  const [contactOpen,      setContactOpen]      = useState(false);
  const [moreSheetOpen,    setMoreSheetOpen]    = useState(false);

  // 4-hour inactivity timeout + its warning modal are mounted once, app-wide,
  // inside SessionGuard (App.jsx) — that covers admin routes too, not just
  // the player shell. See hooks/useInactivityLogout.js.

  // ── Auth listener ──────────────────────────────────────────────────────────
  useEffect(() => {
    return onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      if (!user) navigate('/signin', { replace: true });
    });
  }, [navigate]);

  // ── User profile (displayName, eloRating, status) ─────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(
      doc(db, 'users', currentUser.uid),
      snap => { if (snap.exists()) setUserProfile(snap.data()); },
      () => {}
    );
    return unsub;
  }, [currentUser]);

  // ── Pending scratching alert count (drives notification dot) ──────────────
  useEffect(() => {
    if (!currentUser || !userProfile) return;
    const alerts = userProfile.scratchingAlerts || [];
    const pending = alerts.filter(a =>
      !a.dismissed && a.allocationStatus === 'pending_choice'
    ).length;
    setPendingAlerts(pending);
  }, [userProfile, currentUser]);

  const handleSignOut = useCallback(async () => {
    // Best-effort — the primary defence against a "still shows active"
    // window is server-side staleness (see notificationPreferences.js
    // isUserActive: lastSeenAt older than 5 minutes counts as inactive
    // regardless of this flag), but marking it explicitly on the most
    // common logout path closes that window immediately.
    if (currentUser?.uid) {
      await writePresence(currentUser.uid, { isActive: false, currentPath: location.pathname });
    }
    await signOut(auth);
    navigate('/', { replace: true });
  }, [navigate, currentUser, location.pathname]);

  const showToast = useCallback((label) => {
    setToast(label);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const displayName = userProfile?.displayName
    || currentUser?.displayName
    || currentUser?.email?.split('@')[0]
    || 'Player';

  const eloRating = userProfile?.eloRating || null;
  const profileImageUrl = getProfileImageUrl(userProfile, currentUser);

  if (!currentUser) {
    return (
      <div className="ss-shell-loading">
        <div className="ss-spinner" />
      </div>
    );
  }

  return (
    <ChatDrawerProvider>
    <FriendBadgeSync />
    <div className={`ss-shell ${sidebarCollapsed ? 'ss-shell--collapsed' : ''}`}>

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className="ss-sidebar">

        {/* Logo */}
        <div className="ss-sidebar-logo">
          <Link to="/dashboard" className="ss-logo-link">
            <img src="/favicon.png" alt="" className="ss-logo-img"
                 onError={e => { e.target.style.display = 'none'; }} />
            {!sidebarCollapsed && (
              <div className="ss-logo-text">
                <span className="ss-logo-top">SYNDICATOR</span>
                <span className="ss-logo-bottom">SHOWDOWN</span>
              </div>
            )}
          </Link>
          <button
            className="ss-sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            {sidebarCollapsed ? '›' : '‹'}
          </button>
        </div>

        {/* User profile strip */}
        {!sidebarCollapsed && (
          <div className="ss-sidebar-profile">
            <img
              src={profileImageUrl}
              alt=""
              className="ss-sidebar-avatar ss-sidebar-avatar--img"
              onError={e => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
            />
            <div className="ss-sidebar-user">
              <span className="ss-sidebar-name">{displayName}</span>
              {eloRating && (
                <span className="ss-sidebar-elo">ELO {eloRating}</span>
              )}
            </div>
            {pendingAlerts > 0 && (
              <span className="ss-sidebar-alert-dot">{pendingAlerts}</span>
            )}
            <NotificationBell user={currentUser} />
          </div>
        )}

        {/* Nav items */}
        <nav className="ss-sidebar-nav">
          {NAV_ITEMS.slice(0, -1).map(item => (
            <NavItem
              key={item.path}
              item={item}
              isActive={isActive(item)}
              collapsed={sidebarCollapsed}
              onComingSoon={showToast}
            />
          ))}

          {/* Contact Us — opens modal, matches NavItem styling exactly */}
          <button
            className="ss-nav-item"
            onClick={() => setContactOpen(true)}
            title={sidebarCollapsed ? 'Contact Us' : undefined}
            type="button"
          >
            <span className="ss-nav-icon">✉️</span>
            {!sidebarCollapsed && (
              <span className="ss-nav-label">Contact Us</span>
            )}
          </button>

          {/* Account — last item */}
          <NavItem
            key={NAV_ITEMS[NAV_ITEMS.length - 1].path}
            item={NAV_ITEMS[NAV_ITEMS.length - 1]}
            isActive={isActive(NAV_ITEMS[NAV_ITEMS.length - 1])}
            collapsed={sidebarCollapsed}
            onComingSoon={showToast}
          />
        </nav>

        {/* ── Sidebar footer ─────────────────────────────────── */}
        <div className="sidebar-footer">

          {/* Theme toggle */}
          <div className="sidebar-footer__theme">
            <div className="sidebar-footer__theme-info">
              <span className="sidebar-footer__theme-icon">
                {theme === 'light' ? '☀️' : '🌙'}
              </span>
              {!sidebarCollapsed && (
                <span className="sidebar-footer__theme-label">
                  {theme === 'light' ? 'Track Mode' : 'Night Mode'}
                </span>
              )}
            </div>
            <button
              className={`sidebar-toggle-switch${theme === 'light' ? ' sidebar-toggle-switch--on' : ''}`}
              onClick={toggle}
              role="switch"
              aria-checked={theme === 'light'}
              aria-label="Toggle theme"
              type="button"
            >
              <span className="sidebar-toggle-thumb" />
            </button>
          </div>

          {/* Logout */}
          {!sidebarCollapsed ? (
            <button className="sidebar-footer__logout" onClick={handleSignOut} type="button">
              Logout
            </button>
          ) : (
            <button
              className="sidebar-footer__logout-icon"
              onClick={handleSignOut}
              aria-label="Logout"
              type="button"
            >
              ⏻
            </button>
          )}
        </div>
      </aside>

      {/* ── Main content area ────────────────────────────────────────────── */}
      <main className="ss-main">
        {children}
      </main>

      {/* ── Mobile bottom nav ────────────────────────────────────────────── */}
      <nav className="mobile-nav" aria-label="Main navigation">

        <button
          className={`mobile-nav__item${location.pathname === '/dashboard' ? ' mobile-nav__item--active' : ''}`}
          onClick={() => navigate('/dashboard')}
          aria-label="Dashboard"
          type="button"
        >
          <span className="mobile-nav__icon">🏠</span>
          <span className="mobile-nav__label">Dashboard</span>
        </button>

        <button
          className={`mobile-nav__item${location.pathname.startsWith('/my-stable') ? ' mobile-nav__item--active' : ''}`}
          onClick={() => navigate('/my-stable')}
          aria-label="My Stable"
          type="button"
        >
          <span className="mobile-nav__icon">
            🐴
            {pendingAlerts > 0 && (
              <span className="mobile-nav__badge">{pendingAlerts}</span>
            )}
          </span>
          <span className="mobile-nav__label">My Stable</span>
        </button>

        {/* Raised Enter FAB — centre item */}
        <button
          className="mobile-nav__fab"
          onClick={() => navigate('/showdowns')}
          aria-label="Enter a showdown"
          type="button"
        >
          <span className="mobile-nav__fab-icon">🐎</span>
          <span className="mobile-nav__fab-label">Enter</span>
        </button>

        <button
          className={`mobile-nav__item${location.pathname.startsWith('/leaderboards') ? ' mobile-nav__item--active' : ''}`}
          onClick={() => navigate('/leaderboards')}
          aria-label="Leaderboards"
          type="button"
        >
          <span className="mobile-nav__icon">🥇</span>
          <span className="mobile-nav__label">Leaderboards</span>
        </button>

        <button
          className="mobile-nav__item"
          onClick={() => setMoreSheetOpen(true)}
          aria-label="More options"
          type="button"
        >
          <span className="mobile-nav__icon">···</span>
          <span className="mobile-nav__label">More</span>
        </button>

      </nav>

      {/* ── More bottom sheet ─────────────────────────────────────────────── */}
      {moreSheetOpen && (
        <>
          <div
            className="more-sheet__backdrop"
            onClick={() => setMoreSheetOpen(false)}
          />
          <div className="more-sheet" role="dialog" aria-label="More options">
            <div className="more-sheet__handle" />

            {/* Leaderboards moved to the main bottom nav (position 4) — kept
                out of this sheet so it isn't duplicated in two places. */}
            <button className="more-sheet__item"
              onClick={() => { navigate('/showdowns'); setMoreSheetOpen(false); }}
              type="button">
              <span className="more-sheet__item-icon">🏆</span>
              <span className="more-sheet__item-label">Showdowns</span>
            </button>
            <button className="more-sheet__item"
              onClick={() => { navigate('/market'); setMoreSheetOpen(false); }}
              type="button">
              <span className="more-sheet__item-icon">📈</span>
              <span className="more-sheet__item-label">Market</span>
            </button>
            <button className="more-sheet__item"
              onClick={() => { navigate('/community'); setMoreSheetOpen(false); }}
              type="button">
              <span className="more-sheet__item-icon">👥</span>
              <span className="more-sheet__item-label">Community</span>
            </button>
            <button className="more-sheet__item"
              onClick={() => { navigate('/learn'); setMoreSheetOpen(false); }}
              type="button">
              <span className="more-sheet__item-icon">🎓</span>
              <span className="more-sheet__item-label">Learn</span>
            </button>
            <button className="more-sheet__item"
              onClick={() => { setMoreSheetOpen(false); setContactOpen(true); }}
              type="button">
              <span className="more-sheet__item-icon">✉️</span>
              <span className="more-sheet__item-label">Contact Us</span>
            </button>
            <NotificationBell
              user={currentUser}
              variant="sheet"
              onOpen={() => setMoreSheetOpen(false)}
            />
            <button className="more-sheet__item"
              onClick={() => { navigate('/account'); setMoreSheetOpen(false); }}
              type="button">
              <span className="more-sheet__item-icon">👤</span>
              <span className="more-sheet__item-label">Account</span>
            </button>

            <div className="more-sheet__divider" />

            {/* Theme toggle */}
            <div className="more-sheet__toggle-row">
              <span className="more-sheet__toggle-label">
                {theme === 'light' ? '☀️ Track Mode' : '🌙 Night Mode'}
              </span>
              <button
                className={`more-sheet__toggle-switch${theme === 'light' ? ' more-sheet__toggle-switch--on' : ''}`}
                onClick={toggle}
                role="switch"
                aria-checked={theme === 'light'}
                type="button"
              >
                <span className="more-sheet__toggle-thumb" />
              </button>
            </div>

            {/* Logout */}
            <button className="more-sheet__logout" onClick={handleSignOut} type="button">
              Logout
            </button>
          </div>
        </>
      )}

      {/* ── Coming soon toast ────────────────────────────────────────────── */}
      {toast && (
        <ComingSoonToast label={toast} onDismiss={() => setToast(null)} />
      )}

      {/* ── Chat drawer — renders above everything ────────────────────────── */}
      <ChatDrawer />

      {/* ── Global in-app notification toasts ───────────────────────────── */}
      <NotificationToastHost user={currentUser} />

    </div>

    {/* ── Contact modal — z-index 2001, above everything ───────────────── */}
    <ContactModal
      isOpen={contactOpen}
      onClose={() => setContactOpen(false)}
      prefill={{ subject: 'General Enquiry' }}
    />
    </ChatDrawerProvider>
  );
};

export default AppShell;
